import type { DevicesDevice } from '@smarthome/api';
import { DevicesAvailabilityState, DevicesDeviceType } from '@smarthome/api';
import { formatRelativeTime } from '@smarthome/ui';
import { ChevronDown, ChevronUp, MoreHorizontal, Power, Trash2 } from 'lucide-react';

import { getDeviceIcon, getDeviceOn, getDeviceTypeLabel } from '../utils';

import { Dropdown, IconButton, Select, Tooltip } from '@/components/ui';
import type { DropdownItem } from '@/components/ui';
import { cn } from '@/components/ui/utils';

export interface DeviceRowProps {
  device: DevicesDevice;
  onTogglePower: (device: DevicesDevice, nextOn: boolean) => void;
  onStopWindowCovering: (device: DevicesDevice) => void;
  onPositionChange: (device: DevicesDevice, positionId: string) => void;
  onOpenControl: (device: DevicesDevice) => void;
  onDelete: (device: DevicesDevice) => void;
  isPowerPending?: boolean;
}

const CurtainOpenIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <polyline points="10 6 5 12 10 18" />
    <polyline points="14 6 19 12 14 18" />
  </svg>
);

const CurtainClosedIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <polyline points="5.5 6 10 12 5.5 18" />
    <polyline points="18.5 6 14 12 18.5 18" />
  </svg>
);

const StopFilledIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <rect x="6.5" y="6.5" width="11" height="11" rx="1.5" />
  </svg>
);

export function DeviceRow({
  device,
  onTogglePower,
  onStopWindowCovering,
  onPositionChange,
  onOpenControl,
  onDelete,
  isPowerPending,
}: DeviceRowProps) {
  const Icon = getDeviceIcon(device.type, device.subtype);
  const lastUpdatedText = device.updatedAt ? formatRelativeTime(device.updatedAt) : null;
  const isWindowCovering = device.type === DevicesDeviceType.DeviceTypeWindowCovering;
  const isOn = isWindowCovering ? null : getDeviceOn(device);
  const isAvailable = device.availability === DevicesAvailabilityState.AvailabilityAvailable;
  const isUnavailable = device.availability === DevicesAvailabilityState.AvailabilityUnavailable;
  const windowCoveringPosition = device.state?.windowCovering?.closedPercentage;
  const isFullyOpen = windowCoveringPosition === 0;
  const isFullyClosed = windowCoveringPosition === 100;
  const isWindowCoveringDisabled = !isAvailable || isPowerPending;
  const isCurtainSubtype = device.subtype === 'curtain';
  const OpenIcon = isCurtainSubtype ? CurtainOpenIcon : ChevronUp;
  const CloseIcon = isCurtainSubtype ? CurtainClosedIcon : ChevronDown;
  const coveringIconClass = isCurtainSubtype ? 'h-5 w-5' : 'h-4 w-4';

  // Check if this is a multi-position switch
  const isSwitch = device.type === DevicesDeviceType.DeviceTypeSwitch;
  const positions = device.capabilities?.switch?.positions ?? [];
  const isMultiPosition = isSwitch && positions.length > 0;
  const currentPosition = device.state?.switch?.position;

  const handlePowerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAvailable && !isPowerPending && isOn !== null) {
      onTogglePower(device, !isOn);
    }
  };

  const handleIconClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenControl(device);
  };

  const handlePositionSelect = (positionId: string) => {
    if (isAvailable && !isPowerPending) {
      onPositionChange(device, positionId);
    }
  };

  const actionMenuItems: DropdownItem[] = [
    {
      value: 'delete',
      icon: <Trash2 className="h-4 w-4" />,
      label: 'Delete device',
      variant: 'danger',
    },
  ];

  const handleActionSelect = (value: string) => {
    if (value === 'delete') {
      onDelete(device);
    }
  };

  return (
    <div className="group flex items-center gap-4 border-b border-gray-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-gray-50 dark:border-white/[0.04] dark:hover:bg-white/[0.02]">
      {/* Device Icon - clickable to open controls */}
      <Tooltip content="Device controls">
        <button
          type="button"
          onClick={handleIconClick}
          aria-label="Device controls"
          className="flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
        >
          <Icon className="h-5 w-5" />
        </button>
      </Tooltip>

      {/* Name and subtitle */}
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-sm font-medium text-gray-900 dark:text-white">
          <span className="truncate">{device.name || device.humanId || 'Unnamed Device'}</span>
          {isUnavailable && (
            <span
              className="inline-block h-2 w-2 flex-shrink-0 rounded-full bg-red-500"
              title="Unavailable"
            />
          )}
        </p>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-500">
          <span className="capitalize">{getDeviceTypeLabel(device.type)}</span>
          <span>·</span>
          <span className="capitalize">{device.provider || 'Unknown'}</span>
          {device.humanId && device.name && (
            <>
              <span>·</span>
              <code className="font-mono text-gray-400 dark:text-zinc-600">{device.humanId}</code>
            </>
          )}
          {lastUpdatedText && (
            <>
              <span className="hidden sm:inline">·</span>
              <span className="hidden sm:inline">{lastUpdatedText}</span>
            </>
          )}
        </div>
      </div>

      {/* Power toggle or Position dropdown */}
      {isWindowCovering ? (
        <div
          className={cn(
            'inline-flex overflow-hidden rounded-full border border-gray-200 bg-gray-50 dark:border-white/[0.06] dark:bg-zinc-800',
            isWindowCoveringDisabled && 'opacity-50',
            isPowerPending && 'animate-pulse'
          )}
        >
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isWindowCoveringDisabled) onTogglePower(device, false);
            }}
            disabled={isWindowCoveringDisabled}
            aria-pressed={isFullyOpen}
            aria-label="Open window covering"
            className={cn(
              'flex h-8 w-8 items-center justify-center text-gray-600 transition-colors',
              isFullyOpen
                ? 'bg-white text-gray-900 dark:bg-zinc-900 dark:text-white'
                : 'hover:bg-white/70 dark:text-zinc-400 dark:hover:bg-white/[0.04]',
              isWindowCoveringDisabled && 'cursor-not-allowed'
            )}
          >
            <OpenIcon className={coveringIconClass} />
            <span className="sr-only">Open</span>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isWindowCoveringDisabled) onStopWindowCovering(device);
            }}
            disabled={isWindowCoveringDisabled}
            aria-label="Stop window covering"
            className={cn(
              'flex h-8 w-8 items-center justify-center border-l border-gray-200 text-gray-600 transition-colors',
              'hover:bg-white/70 dark:border-white/[0.08] dark:text-zinc-400 dark:hover:bg-white/[0.04]',
              isWindowCoveringDisabled && 'cursor-not-allowed'
            )}
          >
            <StopFilledIcon className={coveringIconClass} />
            <span className="sr-only">Stop</span>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isWindowCoveringDisabled) onTogglePower(device, true);
            }}
            disabled={isWindowCoveringDisabled}
            aria-pressed={isFullyClosed}
            aria-label="Close window covering"
            className={cn(
              'flex h-8 w-8 items-center justify-center border-l border-gray-200 text-gray-600 transition-colors',
              'dark:border-white/[0.08]',
              isFullyClosed
                ? 'bg-white text-gray-900 dark:bg-zinc-900 dark:text-white'
                : 'hover:bg-white/70 dark:text-zinc-400 dark:hover:bg-white/[0.04]',
              isWindowCoveringDisabled && 'cursor-not-allowed'
            )}
          >
            <CloseIcon className={coveringIconClass} />
            <span className="sr-only">Close</span>
          </button>
        </div>
      ) : isMultiPosition ? (
        <div className={cn(isPowerPending && 'animate-pulse')}>
          <Select
            label="Position"
            hideLabel
            value={currentPosition ?? ''}
            onChange={handlePositionSelect}
            options={positions.map((p) => ({
              value: p.id!,
              label: p.label || p.id || 'Unknown',
            }))}
            disabled={!isAvailable || isPowerPending}
          />
        </div>
      ) : (
        <IconButton
          icon={<Power />}
          label={!isAvailable ? 'Device unavailable' : isOn ? 'Turn off' : 'Turn on'}
          variant="ghost"
          size="sm"
          onClick={handlePowerClick}
          disabled={!isAvailable || isPowerPending || isOn === null}
          className={cn(
            'transition-colors',
            isOn &&
              isAvailable &&
              'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-500/20',
            isOn && isAvailable && 'text-emerald-500 hover:text-emerald-600 dark:text-emerald-400',
            isPowerPending && 'animate-pulse'
          )}
        />
      )}

      {/* Actions menu */}
      <Dropdown
        trigger={
          <IconButton icon={<MoreHorizontal />} label="Device actions" variant="ghost" size="sm" />
        }
        items={actionMenuItems}
        onSelect={handleActionSelect}
        align="right"
      />
    </div>
  );
}
